import type { Signal, SignalFilters, SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { FilterPanelBaseFilter } from "app/pages/sales-intelligence/modules/advanced-search-filtering/models/BaseFilter";
import type { IFilterPanelBaseFilterDeps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filters";
import type SignalsService from "app/pages/sales-intelligence/sub-modules/signals/models/services/SignalsService";
import isUISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/type-guards/isUISignal";
import type { SignalTypePresentationConfig } from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalTypePresentationConfig";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";

export type SignalsFilterOptions = {
    readonly signalType: SignalType;
    readonly availableUISignals: readonly UISignal<SignalFilters>[];
    readonly presentation: SignalTypePresentationConfig;
};

type Deps = IFilterPanelBaseFilterDeps<readonly Signal[], SignalsFilterOptions> & {
    readonly dependencies: {
        readonly signalsService: SignalsService;
    };
};

/**
 * One signal type's filter. Owns the filter-value semantics for its type
 * (0-or-more selected signals); the Signal<->UISignal primitives are delegated to
 * signalsService. Per-type rendering rules (icon/badge/custom view) arrive pre-computed
 * via `presentation` from the SignalsListViewModel.
 */
class SignalsFilter extends FilterPanelBaseFilter<readonly Signal[], SignalsFilterOptions, readonly Signal[]> {
    public readonly signalType: SignalType;
    public readonly presentation: SignalTypePresentationConfig;
    private readonly signalsService: SignalsService;

    constructor(deps: Deps) {
        super(deps);

        this.signalType = deps.options.signalType;
        this.presentation = deps.options.presentation;
        this.signalsService = deps.dependencies.signalsService;
    }

    getEmptyValue(): readonly Signal[] {
        return [];
    }

    isValueEmpty(value?: readonly Signal[]): boolean {
        return !value || value.length === 0;
    }

    isFilterDirty(currentValue: readonly Signal[] | undefined): boolean {
        return !!currentValue && currentValue.length > 0;
    }

    /** The UISignals of this type the user can choose from. */
    get availableUISignals(): readonly UISignal<SignalFilters>[] {
        return this.options.availableUISignals;
    }

    /** The UISignals currently selected by this filter's value. */
    getSelectedUISignals(value: readonly Signal[] | undefined): readonly UISignal<SignalFilters>[] {
        if (!value?.length) {
            return [];
        }

        return value.flatMap((signal) => {
            const resolved = this.resolveSelectedUISignal(signal);

            return resolved ? [resolved] : [];
        });
    }

    private resolveSelectedUISignal(signal: Signal): UISignal<SignalFilters> | undefined {
        if (!signal.id) {
            return undefined;
        }

        const fromCatalog = this.availableUISignals.find((uiSignal) => uiSignal.id === signal.id);

        if (fromCatalog) {
            return fromCatalog;
        }

        return isUISignal(signal) ? signal : undefined;
    }

    /** Toggles a signal in the current selection. */
    toggleSelectedSignal(
        uiSignal: UISignal<SignalFilters>,
        currentValue: readonly Signal[] | undefined,
    ): readonly Signal[] {
        const current = currentValue ?? [];
        const isSelected = current.some((signal) => signal.id === uiSignal.id);

        if (!isSelected) {
            return [...current, this.signalsService.getSignalFromUISignal(uiSignal)];
        }

        return current.filter((signal) => signal.id !== uiSignal.id);
    }

    removeSelectedSignal(signalId: string, currentValue: readonly Signal[] | undefined): readonly Signal[] {
        return (currentValue ?? []).filter((signal) => signal.id !== signalId);
    }
}

export default SignalsFilter;
